<div class="edit-user-container container-fluid p-0">

  <div class="p-4">
    <!-- Page Header (always visible) -->
    <div class="mb-4">
      <app-page-header section="Administration / Users" pageTitle="Edit User" subtitle="Update profile and permissions"
        [actions]="headerActions">
      </app-page-header>
    </div>

    @if(!saveSuccess) {

      <!-- Loading State -->
      @if(loading) {
      <div class="d-flex justify-content-center align-items-center py-5">
        <div class="spinner-border text-secondary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      }

      <!-- Unsaved Changes Warning Bar -->
      @if(!loading && diff.length > 0) {
      <div class="unsaved-bar d-flex justify-content-between align-items-center px-4 py-2 mb-2">
        <div class="d-flex align-items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#D97706" viewBox="0 0 16 16">
            <path
              d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
          </svg>
          <span class="unsaved-text">
            You have <strong>{{ diff.length }} unsaved change</strong>{{ diff.length > 1 ? 's' : '' }}. Leaving this page
            will discard them.
          </span>
        </div>
        <button type="button" class="btn btn-link discard-link p-0" (click)="onDiscardAll()">Discard all</button>
      </div>
      }

      @if(!loading) {
      <div class="row g-4">

        <!-- Left Column: Form -->
        <div class="col-lg-8 col-md-12">
          <form (submit)="$event.preventDefault(); onSave()" novalidate>
            <fieldset [disabled]="saving" class="border-0 p-0 m-0">

              <!-- Section 01: Identity -->
              <div class="form-section mb-4">
                <div class="section-header d-flex justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center gap-2">
                    <span class="section-num">01</span>
                    <span class="section-title">Identity</span>
                    @if(isSectionModified('identity')) {
                    <span class="modified-badge">
                      <span class="modified-dot"></span>Modified
                    </span>
                    }
                  </div>
                  <span class="section-subtitle">Personal &amp; contact details</span>
                </div>

                <div class="section-content">
                  <app-full-name-input [(value)]="formData.fullName" [hasError]="submitted && fullNameError"
                    (valueChange)="fullNameError = false">
                  </app-full-name-input>
                  @if(isFieldModified('Full name')) {
                  <div class="field-hint">
                    <span class="hint-old">Previously {{ getFieldDiff('Full name')?.oldValue }}</span>
                    <span class="hint-arrow"> → </span>
                    <span class="hint-updated">updated</span>
                  </div>
                  }

                  <div class="row mt-3">
                    <div class="col-md-6 col-12">
                      <app-email-input [(value)]="formData.email" [(validationState)]="emailState"
                        [originalValue]="originalData.email">
                      </app-email-input>
                      @if(isFieldModified('Email')) {
                      <div class="field-hint">
                        <span class="hint-old">Previously {{ getFieldDiff('Email')?.oldValue }}</span>
                        <span class="hint-arrow"> → </span>
                        <span class="hint-updated">updated</span>
                      </div>
                      }
                    </div>
                    <div class="col-md-6 col-12">
                      <app-phone-input [(value)]="formData.phone" [(validationState)]="phoneState"
                        [originalValue]="originalData.phone">
                      </app-phone-input>
                      @if(isFieldModified('Phone')) {
                      <div class="field-hint">
                        <span class="hint-old">Previously {{ getFieldDiff('Phone')?.oldValue }}</span>
                        <span class="hint-arrow"> → </span>
                        <span class="hint-updated">updated</span>
                      </div>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div class="section-divider"></div>

              <!-- Section 02: Role & Access -->
              <div class="form-section mb-4">
                <div class="section-header d-flex justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center gap-2">
                    <span class="section-num">02</span>
                    <span class="section-title">Role &amp; access</span>
                    @if(isSectionModified('role-access')) {
                    <span class="modified-badge">
                      <span class="modified-dot"></span>Modified
                    </span>
                    }
                  </div>
                  <span class="section-subtitle">Defines what this user can do</span>
                </div>

                <div class="section-content">
                  <app-supervisory-level-selector [selectedLevel]="formData.supervisoryLevel"
                    [hasError]="submitted && supervisoryLevelError" (roleChange)="onLevelChange($event)">
                  </app-supervisory-level-selector>
                  @if(isFieldModified('Supervisory Level')) {
                  <div class="field-hint">
                    <span class="hint-old">Previously {{ getSupervisoryLevelLabel(originalData.supervisoryLevel) }}</span>
                    <span class="hint-arrow"> → </span>
                    <span class="hint-updated">updated</span>
                  </div>
                  }

                  <app-role-select [value]="formData.role ? formData.role.id : null" [hasError]="submitted && roleError"
                    (valueChange)="onRoleChange($event)">
                  </app-role-select>
                  @if(isFieldModified('Role')) {
                  <div class="field-hint">
                    <span class="hint-old">Previously {{ getFieldDiff('Role')?.oldValue }}</span>
                    <span class="hint-arrow"> → </span>
                    <span class="hint-updated">updated</span>
                  </div>
                  }

                  <app-access-type-selector [selectedTypes]="[formData.PlatformAccess]"
                    (typesChange)="formData.PlatformAccess = $event[0]">
                  </app-access-type-selector>
                  @if(isFieldModified('Access Type')) {
                  <div class="field-hint">
                    <span class="hint-old">Previously {{ getFieldDiff('Access Type')?.oldValue }}</span>
                    <span class="hint-arrow"> → </span>
                    <span class="hint-updated">updated</span>
                  </div>
                  }
                </div>
              </div>

              <div class="section-divider"></div>

              <!-- Section 03: Hierarchy & Territory -->
              <div class="form-section mb-4">
                <div class="section-header d-flex justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center gap-2">
                    <span class="section-num">03</span>
                    <span class="section-title">Hierarchy &amp; territory</span>
                    @if(isSectionModified('hierarchy')) {
                    <span class="modified-badge">
                      <span class="modified-dot"></span>Modified
                    </span>
                    }
                  </div>
                  <span class="section-subtitle">Hierarchy &amp; assignment</span>
                </div>

                <div class="section-content">
                  <app-supervisor-picker [supervisor]="formData.supervisor" [hasError]="submitted && supervisorError"
                    [disabled]="formData.supervisoryLevel === 'L3'"
                    [disabledMessage]="'Regional Manager (L3) does not report to anyone'"
                    (pickSupervisor)="onPickSupervisor()">
                  </app-supervisor-picker>
                  @if(showSupervisoryLevelRequiredError) {
                  <div class="text-danger small mt-1 mb-2 d-flex align-items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor"
                      class="bi bi-exclamation-circle" viewBox="0 0 16 16">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path
                        d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                    </svg>
                    <span>Please select a supervisory level first.</span>
                  </div>
                  }
                  @if(isFieldModified('Supervisor')) {
                  <div class="field-hint">
                    <span class="hint-old">Previously {{ getFieldDiff('Supervisor')?.oldValue }}</span>
                    <span class="hint-arrow"> → </span>
                    <span class="hint-updated">updated</span>
                  </div>
                  }

                  <div class="mt-3">
                    <label class="form-label fw-semibold small">Geographic Territory</label>
                    <app-geographic-territory-tree
                      [selectedIds]="formData.geographicNodeIds || []"
                      [nodes]="geoNodes"
                      [loading]="geoLoading"
                      [isModified]="isFieldModified('Territory')"
                      [originalNodeCount]="originalData.geographicNodeIds?.length || 0"
                      (selectionChange)="onGeoSelectionChange($event)"
                      (searchChange)="onGeoSearch($event)">
                    </app-geographic-territory-tree>
                  </div>
                </div>
              </div>

            </fieldset>
          </form>
        </div>

        <!-- Right Column: Pending Changes Panel -->
        <div class="col-lg-4 col-md-12">
          <div class="sticky-top-panel">

            <!-- 409 Conflict Panel -->
            @if(saveError) {
            <div class="card border-warning mb-4 conflict-card">
              <div class="card-body p-4 text-center">
                <div class="lock-icon-container mb-3 mx-auto">
                  <span class="fs-4">🔒</span>
                </div>
                <h5 class="card-title fw-bold text-dark mb-1">Could not save changes</h5>
                <p class="text-muted small mb-4">A conflict was detected at save time</p>
                <div class="conflict-detail-card p-3 rounded text-start mb-4">
                  <p class="mb-0 text-secondary small">{{ saveError.message }}</p>
                </div>
                <div class="d-flex gap-2 justify-content-center mb-3">
                  <button type="button" class="btn btn-outline-secondary btn-sm px-3"
                    (click)="onDiscardConflict()">Discard</button>
                  @if(saveError.type === 'phone_conflict') {
                  <button type="button" class="btn btn-warning text-white btn-sm px-3"
                    (click)="onEditPhoneConflict()">Edit phone</button>
                  }
                  @if(saveError.type === 'email_conflict') {
                  <button type="button" class="btn btn-warning text-white btn-sm px-3"
                    (click)="onEditEmailConflict()">Edit email</button>
                  }
                </div>
                <div class="text-muted small border-top pt-3 error-code">
                  Error &middot; 409 {{ saveError.code || 'CONFLICT_DUPLICATE' }}
                </div>
              </div>
            </div>
            }

            <!-- Preview Card (User Card + Pending Changes) -->
            <div class="card preview-card p-4 mb-4 shadow-sm">

              <!-- A. User Card -->
              <div class="user-card-preview p-3 d-flex align-items-center gap-3 mb-4">
                <div class="preview-avatar" [class.empty]="!formData.fullName">
                  {{ formData.fullName || '—' | initials }}
                </div>
                <div class="overflow-hidden">
                  <div class="fw-bold preview-name text-truncate">{{ formData.fullName || 'Unnamed user' }}</div>
                  <div class="preview-role text-muted small text-truncate">
                    {{ getSupervisoryLevelLabel(formData.supervisoryLevel) }}
                    @if(formData.supervisor) {
                    <span>&middot; reports to {{ formData.supervisor.name }}</span>
                    }
                  </div>
                </div>
              </div>

              <!-- B. Pending Changes -->
              <div class="pending-changes-section">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <span class="small-title text-uppercase text-muted fw-bold">Pending Changes</span>
                  @if(diff.length > 0) {
                  <span class="badge bg-warning-subtle text-warning-amber border border-warning-subtle">{{ diff.length
                    }}</span>
                  }
                </div>

                @if(diff.length === 0) {
                <p class="placeholder-text">Edit fields on the left to track changes here. The diff updates as you
                  type.</p>
                }

                @if(diff.length > 0) {
                <div class="d-flex flex-column gap-2">
                  @for(change of diff; track change.field) {
                  <div class="diff-card">
                    <div class="diff-field-label">{{ change.field }}</div>
                    <div class="diff-values d-flex align-items-center gap-1 flex-wrap">
                      <span class="diff-old">{{ change.oldValue }}</span>
                      <span class="diff-arrow">→</span>
                      <span class="diff-new">{{ change.newValue }}</span>
                    </div>
                  </div>
                  }
                </div>
                }
              </div>

              <!-- C. Access Impact (when supervisory level changes) -->
              @if(isFieldModified('Supervisory Level') && formData.supervisoryLevel) {
              <div class="access-impact-section border-top mt-4 pt-4">
                <div class="small-title text-uppercase text-muted fw-bold mb-3">Access Impact</div>
                <ul class="list-unstyled mb-0 d-flex flex-column gap-2">
                  @for(perm of ROLE_PERMISSIONS[formData.supervisoryLevel!]; track $index) {
                  <li class="permission-item d-flex align-items-center gap-2">
                    <span class="bullet-dot">●</span>
                    <span class="secondary-text">{{ perm }}</span>
                  </li>
                  }
                </ul>
              </div>
              }

            </div>
          </div>
        </div>

      </div>
      }

    } @else {

      
      <!-- ── Success View ─────────────────────────────────────────────────── -->
      <div class="success-view">

        <!-- Checkmark circle -->
        <div class="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
            <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
          </svg>
        </div>

        <h2 class="success-title">User updated</h2>

        <p class="success-subtitle">
          <strong>{{ saveSuccessSummary?.totalChanged }} field{{ (saveSuccessSummary?.totalChanged ?? 0) > 1 ? 's' : '' }} modified</strong>
          <span class="success-subtitle-dim"> · changes are now live</span>
        </p>

        <!-- Change summary -->
        <ul class="success-changes">
          @for(change of saveSuccessSummary?.changes; track change.label) {
          <li class="success-change-row">
            <span class="change-label">{{ change.label }}</span>
            <span class="change-summary">{{ change.summary }}</span>
          </li>
          }
        </ul>

        <!-- Actions -->
        <div class="success-actions">
          <button type="button" class="btn btn-outline-secondary logs-btn" (click)="onViewActivityLog()">View activity log</button>
          <button type="button" class="btn btn-teal save-btn" (click)="onDone()">Done</button>
        </div>

        <p class="success-countdown">Returns to user list in {{ countdownDisplay }}s</p>

      </div>
    }
  </div>

  <!-- Supervisor Picker Modal (form state only) -->
  @if(!loading && !saveSuccess) {
  <app-supervisor-picker-modal [isOpen]="showSupervisorModal" [supervisoryLevel]="formData.supervisoryLevel"
    [selectedSupervisorId]="formData.supervisorId" (supervisorSelected)="onSupervisorSelected($event)"
    (closed)="closeSupervisorModal()">
  </app-supervisor-picker-modal>
  }

</div>
